import { useEffect, useState, type ChangeEvent } from 'react'
import {
  formatPlNumber,
  parsePlNumber,
  sanitizeNumericInput,
} from '../../components/numberFormat'

/**
 * Minimal input for an annex pending change. Mirrors the basic FieldType branches from
 * FieldRenderer but works against the lightweight AnnexableFieldDto (no options /
 * validation rules / file plumbing). NUMBER and CURRENCY share the PL-formatted text
 * input with optional "zł" suffix.
 */
export function SimpleFieldInput({
  fieldType,
  value,
  onChange,
  disabled,
}: {
  fieldType: string
  value: unknown
  onChange: (v: unknown) => void
  disabled?: boolean
}) {
  const common = inputCommon(disabled)

  switch (fieldType) {
    case 'FILE':
    case 'GALLERY':
      // Files are managed in the dedicated Files tab; an annex never carries
      // a file change in its change list. The picker filters these out -- this
      // branch only catches stale entries from older drafts.
      return (
        <div className="text-xs text-amber-700 px-2 py-1.5 bg-amber-50 rounded border border-amber-200">
          Pliki zmienia się w zakładce "Pliki". Usuń to pole z listy zmian.
        </div>
      )
    case 'BOOLEAN':
      return (
        <input
          type="checkbox"
          checked={!!value}
          disabled={disabled}
          onChange={e => onChange(e.target.checked)}
        />
      )
    case 'NUMBER':
      return <NumericInput value={value} onChange={onChange} disabled={disabled} />
    case 'CURRENCY':
      return <NumericInput value={value} onChange={onChange} disabled={disabled} decimals={2} suffix="zł" />
    case 'DATE':
      return (
        <input
          type="date"
          className={common}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={e => onChange(e.target.value || null)}
        />
      )
    case 'TEXTAREA':
      return (
        <textarea
          className={`${common} min-h-[4rem]`}
          disabled={disabled}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={e => onChange(e.target.value)}
        />
      )
    default:
      return (
        <input
          type="text"
          className={common}
          disabled={disabled}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={e => onChange(e.target.value)}
        />
      )
  }
}

function inputCommon(disabled?: boolean) {
  return [
    'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm',
    'focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15',
    disabled ? 'cursor-not-allowed bg-slate-50 text-slate-500' : '',
  ].join(' ')
}

/**
 * PL-formatted numeric input (comma or dot as decimal separator). Commits to a number
 * on blur; empty string clears to null. {@code decimals=2} + {@code suffix="zł"} for
 * CURRENCY mirrors FieldRenderer's NumericInput.
 */
function NumericInput({
  value,
  onChange,
  disabled,
  decimals,
  suffix,
}: {
  value: unknown
  onChange: (v: unknown) => void
  disabled?: boolean
  decimals?: number
  suffix?: string
}) {
  const [raw, setRaw] = useState<string>(() => initialRaw(value, decimals))

  useEffect(() => {
    const incoming = initialRaw(value, decimals)
    setRaw(prev => (sameNumber(prev, incoming, decimals) ? prev : incoming))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRaw(sanitizeNumericInput(e.target.value, decimals !== 0))
  }

  const commit = () => {
    if (raw.trim() === '') {
      onChange(null)
      return
    }
    const parsed = parsePlNumber(raw)
    if (parsed === null || Number.isNaN(parsed)) return
    const rounded =
      decimals === undefined ? parsed : Math.round(parsed * 10 ** decimals) / 10 ** decimals
    onChange(rounded)
    setRaw(formatPlNumber(rounded, decimals))
  }

  return (
    <div className="relative flex items-center">
      <input
        type="text"
        inputMode="decimal"
        className={[
          inputCommon(disabled),
          suffix ? 'pr-12 text-right tabular-nums' : '',
        ].join(' ')}
        disabled={disabled}
        value={raw}
        onChange={handleChange}
        onBlur={commit}
      />
      {suffix && (
        <span className="absolute inset-y-0 right-0 flex items-center pl-2 pr-3 text-xs font-medium text-slate-500 border-l border-slate-200 pointer-events-none select-none">
          {suffix}
        </span>
      )}
    </div>
  )
}

function initialRaw(value: unknown, decimals: number | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number') return formatPlNumber(value, decimals)
  return String(value)
}

function sameNumber(prevRaw: string, nextRaw: string, decimals: number | undefined): boolean {
  const a = parsePlNumber(prevRaw)
  const b = parsePlNumber(nextRaw)
  if (a === null && b === null) return true
  if (a === null || b === null) return false
  if (Number.isNaN(a) || Number.isNaN(b)) return prevRaw === nextRaw
  if (decimals === undefined) return a === b
  const factor = 10 ** decimals
  return Math.round(a * factor) === Math.round(b * factor)
}
